/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Button, Tooltip, rem, useMantineTheme } from '@mantine/core';
import { RiCloseCircleFill, RiFileCopyLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useChangeFavicon } from '@fai/friendli-suite/features/common/hooks/use-change-favicon';
import {
  isFinetuningDeleteable,
  isFinetuningDeployable,
  isFinetuningCancelable,
  isFinetuningDeployTooltipVisible,
  getFaviconTypeFromFinetuningStatus,
} from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/finetuning-status';
import { useFinetuningStatus } from '@fai/friendli-suite/features/products/dedicated-endpoints/finetuning/use-finetuning-status';
import Paths from '@fai/friendli-suite/features/routes/paths';
import TransparentBackLinkButton from '@fai/friendli-suite/views/common/button/back-button/transparent-link';
import FinetuningTag from '@fai/friendli-suite/views/dedicated-endpoints/projects/finetuning/finetuning-tag';
import DEProjectSubHeader from '@fai/friendli-suite/views/layout/product/dedicated-endpoints/projects/sub-layout/header';

import { openCancelFinetuningModal } from './cancel-finetuning-modal';
import { openDeleteFinetuningModal } from './delete-finetuning-modal';
import { openRenameFinetuningModal } from './rename-finetuning-modal';
import { useFinetuningDetailHeader } from './use-finetuning-detail-header';

export default function FinetuningDetailHeader({ outputModelCount }: { outputModelCount: number }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId = '', projectId = '', finetuningId = '' } = useParams();

  const { status } = useFinetuningStatus();
  const { data } = useFinetuningDetailHeader();

  useChangeFavicon(getFaviconTypeFromFinetuningStatus(status));

  const name = data.name ?? '(name)';
  const listPath = Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).finetuning.list;

  const navigateToCreateFinetuningWithId = () => {
    // navigate to create finetuning page with finetuning form fields to duplicate.
    // Ref: apps/friendli-suite/src/pages/dedicated-endpoints/projects/finetuning/create/use-get-initial-data.ts
    navigate(Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).finetuning.create, {
      state: { finetuningId },
    });
  };

  const navigateToCreateEndpointWithFinetuning = () => {
    // navigate to create endpoint page with pre-selected model and fine-tuned adapter.
    // Ref: apps/friendli-suite/src/pages/dedicated-endpoints/projects/endpoints/create/use-get-initial-data.ts
    navigate(Paths.team(teamId).dedicatedEndpoints.project.detail(projectId).endpoint.create, {
      state: { model: data.model, finetuningName: name },
    });
  };

  const canDeployEndpoint = isFinetuningDeployable(status) && outputModelCount > 0;

  const cancelButton = (
    <Button
      variant="pill"
      size="sm"
      leftSection={<RiCloseCircleFill size={16} />}
      onClick={() => openCancelFinetuningModal()}
    >
      {t('shared.cancel')}
    </Button>
  );

  const duplicateButton = (
    <Button
      variant="outline"
      size="sm"
      leftSection={<RiFileCopyLine size={16} />}
      onClick={navigateToCreateFinetuningWithId}
    >
      {t('dedicated_endpoints.project.duplicate')}
    </Button>
  );

  const deployButton = (
    <Tooltip
      label={t('dedicated_endpoints.project.finetuning_deploy_tooltip_message')}
      maw={rem(280)}
      disabled={!isFinetuningDeployTooltipVisible(status) || canDeployEndpoint}
    >
      <Button
        theme="cloud"
        size="sm"
        miw={rem(80)}
        disabled={!canDeployEndpoint}
        onClick={navigateToCreateEndpointWithFinetuning}
      >
        {t('dedicated_endpoints.project.deploy')}
      </Button>
    </Tooltip>
  );

  const EllipsisMenu = (
    <DEProjectSubHeader.EllipsisMenu>
      <DEProjectSubHeader.EllipsisMenuItem
        onClick={() => openRenameFinetuningModal({ finetuningName: name })}
      >
        {t('shared.rename')}
      </DEProjectSubHeader.EllipsisMenuItem>

      <DEProjectSubHeader.EllipsisMenuItem
        c={theme.pf.color.fg.danger.default.base}
        disabled={!isFinetuningDeleteable(status)}
        onClick={() => openDeleteFinetuningModal({ finetuningName: name })}
      >
        {t('shared.delete')}
      </DEProjectSubHeader.EllipsisMenuItem>
    </DEProjectSubHeader.EllipsisMenu>
  );

  return (
    <DEProjectSubHeader
      titleSlot={
        <DEProjectSubHeader.TitleRow
          title={name}
          leftSlot={
            <>
              <FinetuningTag showPhase={true} />
              {isFinetuningCancelable(status) && cancelButton}
            </>
          }
          rightSlot={
            <>
              {duplicateButton}
              {deployButton}
              {EllipsisMenu}
            </>
          }
        />
      }
      backButtonSlot={
        <TransparentBackLinkButton to={listPath}>
          {t('dedicated_endpoints.project.back_to_finetuning')}
        </TransparentBackLinkButton>
      }
    />
  );
}

FinetuningDetailHeader.displayname = 'FinetuningDetailHeader';
